import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment, useState } from 'react';

import type { Step } from '@sorare/core/src/components/StepperDialog';
import { StepperDialog } from '@sorare/core/src/components/StepperDialog';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

import { ChooseYourRewardStory } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskStatus/ChooseYourRewardStory/index.tsx';
import type { RewardUnlockedStory_reward } from '#src/components/missions/stories/RewardUnlockedStory/__generated__/index.graphql.ts';
import { RewardUnlockedStory } from '#src/components/missions/stories/RewardUnlockedStory/index.tsx';
import { useClaimTask } from '#src/components/missions/useClaimTask.tsx';

import { ClaimYourStreakRewardsStory } from './ClaimYourStreakRewardsStory/index.tsx';
import type { ThresholdsStreakTaskClaimStory_thresholdsStreakTask } from './__generated__/index.graphql.ts';

type Props = {
  task: ThresholdsStreakTaskClaimStory_thresholdsStreakTask;
  onClose: () => void;
};
export const ThresholdsStreakTaskClaimStory = ({ task, onClose }: Props) => {
  const [claimTask] = useClaimTask();
  const { sport } = useSportContext();
  const [rewards, setRewards] = useState<RewardUnlockedStory_reward[]>([]);
  const [signedAlternativeRewardConfigs, setSignedAlternativeRewardConfigs] =
    useState<string | undefined>();

  const { isMobileApp, postMessage } = useIsMobileApp();

  const rewardConfigs = task.rewardConfigs || [];
  const alternativeRewardConfigs = task.alternativeRewardConfigs || [];
  const canChooseAlternativeRewardConfigs = alternativeRewardConfigs.length > 0;

  return (
    <StepperDialog
      onClose={() => {
        if (isMobileApp) {
          postMessage('taskClaimed', { taskId: task.id });
        }
        onClose();
      }}
      steps={[
        {
          bodyElement: ({ onNext }) => (
            <Fragment key="claim-streak-rewards-story">
              <ClaimYourStreakRewardsStory
                task={task}
                onContinue={async () => {
                  if (!canChooseAlternativeRewardConfigs) {
                    const { data } = await claimTask({
                      variables: {
                        taskId: task.id,
                        sport,
                      },
                    });
                    setRewards(data?.claimTask?.rewards || []);
                  }
                  onNext();
                }}
              />
            </Fragment>
          ),
        },
        ...(canChooseAlternativeRewardConfigs
          ? [
              {
                bodyElement: ({ onNext }) => (
                  <Fragment key="choose-reward-story">
                    <ChooseYourRewardStory
                      rewardConfigs={rewardConfigs}
                      alternativeRewardConfigs={alternativeRewardConfigs}
                      rewardMultiplier={task.rewardMultiplier}
                      setSignedAlternativeRewardConfigs={
                        setSignedAlternativeRewardConfigs
                      }
                      onConfirm={async () => {
                        const { data } = await claimTask({
                          variables: {
                            taskId: task.id,
                            signedAlternativeRewardConfigs,
                            sport,
                          },
                        });
                        setRewards(data?.claimTask?.rewards || []);
                        onNext();
                      }}
                    />
                  </Fragment>
                ),
              } as Step,
            ]
          : []),
        {
          bodyElement: ({ onNext }) => (
            <Fragment key="reward-unlocked-story">
              <RewardUnlockedStory
                onClose={onNext}
                rewards={rewards}
                fallbackRewardConfigs={
                  alternativeRewardConfigs.find(
                    alternative =>
                      alternative.signedAlternativeRewardConfigs ===
                      signedAlternativeRewardConfigs
                  )?.rewardConfigs ?? rewardConfigs
                }
              />
            </Fragment>
          ),
        },
      ]}
    />
  );
};

ThresholdsStreakTaskClaimStory.fragments = {
  taskInterface: gql`
    fragment ThresholdsStreakTaskClaimStory_thresholdsStreakTask on ThresholdsStreakTask {
      id
      rewardConfigs {
        id
        ...ChooseYourRewardStory_rewardConfig
        ...RewardUnlockedStory_rewardConfig
      }
      rewardMultiplier
      alternativeRewardConfigs {
        ...ChooseYourRewardStory_alternativeRewardConfig
        rewardConfigs {
          id
          ...RewardUnlockedStory_rewardConfig
        }
      }
      ...ClaimYourStreakRewardsStory_taskInterface
    }
    ${ClaimYourStreakRewardsStory.fragments.taskInterface}
    ${ChooseYourRewardStory.fragments.rewardConfig}
    ${ChooseYourRewardStory.fragments.alternativeRewardConfig}
    ${RewardUnlockedStory.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<ThresholdsStreakTaskClaimStory_thresholdsStreakTask>,
};
